import { useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { useAppDispatch } from "store/hooks";
import {
  createIbanInvoice,
  setCreatedInvoiceUrl,
  setShowSuccessModal,
} from "store/features/ibanInvoiceSlice";

import { routes } from "routes/routes";
import { FormValues } from "../validation";
import paymentPurposeService from "services/paymentPurpose";
import { IbanInvoiceAddCommand, IbanInvoiceValidatedResponseDto } from "api";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";
import { getIbanAccounts } from "store/features/ibanAccountSlice";
import posthog from "posthog-js";

export const useInvoiceActions = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [submitting, setSubmitting] = useState(false);

  const prepareInvoicePayload = (data: FormValues): IbanInvoiceAddCommand => {
    const {
      showNotes,
      showClientNotes,
      showInvoiceExpiration,
      expirationDays,
      expirationHours,
      requestPayerFullName,
      requestOrderNumber,
      ibanAccountUid,
      ...rest
    } = data;

    const expiration =
      showInvoiceExpiration && (expirationDays || expirationHours)
        ? { days: expirationDays || 0, hours: expirationHours || 0 }
        : undefined;

    const paymentPurposeOptions = {
      requestPayerFullName: requestPayerFullName || false,
      requestOrderNumber: requestOrderNumber || false,
    };

    return {
      ...rest,
      expiration,
      paymentPurposeOptions,
      notes: showNotes ? data.notes : "",
      clientNotes: showClientNotes ? data.clientNotes : "",
      ibanAccountUid:
        ibanAccountUid && ibanAccountUid !== "new" ? ibanAccountUid : null,
    };
  };

  const trackInvoiceCreation = (response: IbanInvoiceValidatedResponseDto) => {
    const properties = {
      ibanInvoiceId: response?.ibanQrInvoiceUid || "",
      bankMFO: response?.bank?.mfoCode || "",
      invoiceAmount: response?.amount || 0,
      identificationCode: response?.identificationCode || "",
      hasValidData: response.isValidIban || response.isValidIdentificationCode,
    };

    analytics().sendEvent(AnalyticsEvents.cabinetIbanCreated, {
      ...properties,
      iban: response?.iban || "",
    });
    analytics().sendPosthogEvent(AnalyticsEvents.cabinetIbanCreated, {
      ...properties,
      iban: response?.iban || "",
    });
    analytics().sendPixelEvent(
      AnalyticsEvents.cabinetIbanCreated,
      properties,
      true,
    );
  };

  const onSubmit = async (data: FormValues) => {
    const payload = prepareInvoicePayload(data);

    const isCreatingNewAccount = !payload?.ibanAccountUid;
    const destination = location.state?.from || routes.invoices;

    try {
      setSubmitting(true);

      const response = await dispatch(
        createIbanInvoice({ body: payload }),
      ).unwrap();

      if (
        !response.paymentPurposeOptions.requestOrderNumber &&
        !response.paymentPurposeOptions.requestPayerFullName
      ) {
        paymentPurposeService().savePaymentPurpose(response.paymentPurpose);
      }

      if (isCreatingNewAccount && destination !== routes.accounts) {
        await dispatch(getIbanAccounts()).unwrap();
      }

      trackInvoiceCreation(response);

      dispatch(setCreatedInvoiceUrl(response.ibanInvoiceUrl || ""));
      dispatch(setShowSuccessModal(true));

      const from = location.state?.from || routes.invoices;

      navigate(from);
    } catch (error) {
      posthog.captureException(error, {
        context: "issue with IBAN invoice creation",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onGoBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(routes.invoices, { replace: true });
    }
  };

  return {
    onSubmit,
    onGoBack,
    submitting,
  };
};
